/**
 * PM-1833: entitlement-driven onboarding-v2 field/document requirements,
 * derived from the "[merged] Onboarding Registration Requirements Matrix"
 * Google Sheet (per-tier column values: Required / Conditional / Not
 * Required / Omit / Prefill). This is the single source of truth both the
 * onboarding-v2 form (frontend/onboarding-v2/src/entitlements.ts,
 * formSpec.ts) and the backend document-packet filter
 * (backend/core/src/services/onboarding-docuseal/onboarding-docuseal.class.ts)
 * read — do not duplicate these tables elsewhere.
 *
 * Tier names here are the exact HubSpot subscription-tier strings from
 * frontend/onboarding-v2/src/tiers.ts (PM-1917) — not invented here.
 * `ALIASES` mirrors that file's legacy-value map; keep the two in sync.
 */
/**
 * Fields the sheet varies by tier, for the "full registration" columns
 * (Explorer / Athena's Wisdom). A field absent from this table is
 * tier-universal — e.g. NPI, PMS, DEA/HIN, pharmacy address (Prefill/Show),
 * primary wholesaler contact info (Prefill/Hide) — and is governed entirely
 * by its own formSpec/packet rule, unaffected by tier.
 */
const FULL_REGISTRATION = {
    'dateBusinessBegan': 'required',
    'pharmacyType': 'required',
    'dispensing.fillOutOfStateCustomers': 'required',
    'dispensing.outOfStateCustomersCount': 'required',
    'stateLicense': 'required',
    'stateLicenseExpDate': 'required',
    'stateIdNumber': 'required',
    'federalTaxNumber': 'required',
    'stateSellersPermit': 'required',
    'gln': 'required',
    'dunsNumber': 'not-required',
    'medicarePartB': 'required',
    'owner.firstName': 'required',
    'owner.lastName': 'required',
    'owner.phone': 'required',
    'owner.social': 'required',
    'owner.percentOwned': 'required',
    'owner.ownershipType': 'required',
    'owner.address': 'required',
    'owner.city': 'required',
    'owner.state': 'required',
    'owner.zip': 'required',
    'pic.firstName': 'required',
    'pic.lastName': 'required',
    'pic.email': 'required',
    'pic.phone': 'required',
    'pic.license': 'required',
    'pic.licenseExpDate': 'required',
    'pp.firstName': 'required',
    'pp.lastName': 'required',
    'pp.title': 'required',
    'pp.email': 'required',
    'pp.phone': 'required',
    'pp.fax': 'required',
    'ap.firstName': 'required',
    'ap.lastName': 'required',
    'ap.email': 'required',
    'ap.phone': 'required',
    'banking.bankName': 'required',
    'banking.accountType': 'required',
    'banking.accountOfficer': 'required',
    'banking.bankPhone': 'required',
    'banking.bankAddress': 'required',
    'banking.bankCity': 'required',
    'banking.bankState': 'required',
    'banking.bankZip': 'required',
    'banking.receiveInvoices': 'required',
    'banking.accountToBeDebited': 'not-required',
    'banking.accountToBeDebitedOther': 'not-required',
    'banking.monthlyChargeAmount': 'not-required',
    'payments.billingAddress': 'required',
    'payments.billingCity': 'required',
    'payments.billingState': 'required',
    'payments.billingZip': 'required',
    'payments.defaultMethod': 'required',
    // PSAO / buying group: the one field the sheet flips — required only for
    // Athena's Justice (below), Not Required for the full-registration tiers.
    'primary.psao': 'not-required',
    'tradeReference1.name': 'required',
    'tradeReference1.accountNumber': 'required',
    'tradeReference1.phone': 'required',
    'tradeReference1.address': 'required',
    'tradeReference1.city': 'required',
    'tradeReference1.state': 'required',
    'tradeReference1.zip': 'required',
    'tradeReference2.name': 'required',
    'tradeReference2.phone': 'required',
    'tradeReference2.address': 'required',
    'tradeReference2.city': 'required',
    'tradeReference2.state': 'required',
    'tradeReference2.zip': 'required',
    'shippingAddress': 'required',
    'shippingCity': 'required',
    'shippingState': 'required',
    'shippingZip': 'required',
    'multipleShipToFacilities': 'not-required',
    'relatedToOtherFacilities': 'not-required',
    'relatedToOtherFacilitiesList': 'not-required',
    // The explanation fields have no dedicated sheet row — they inherit
    // 'required' so the caller's own yes/no-conditioned requiredWhen decides
    // (see frontend/onboarding-v2/src/formSpec.ts's `g` helper), rather than
    // being force-relaxed independent of the answer.
    'convicted': 'required',
    'convictedExplanation': 'required',
    'bankruptcy': 'required',
    'bankruptcyExplanation': 'required',
};
/** Explorer and Athena's Wisdom are identical "full registration" columns. */
const EXPLORER = FULL_REGISTRATION;
const WISDOM = FULL_REGISTRATION;
/**
 * Athena's Justice omits the whole registration/ownership/banking/shipping/
 * trade-reference packet — only the tier-universal fields (NPI, PMS,
 * DEA/HIN, primary wholesaler, signer, etc.) apply. PSAO is the sheet's one
 * exception: required for Justice, unlike every other tier.
 */
const JUSTICE = Object.fromEntries(Object.keys(FULL_REGISTRATION).map((path) => [path, 'omit']));
JUSTICE['primary.psao'] = 'required';
/**
 * Athena's Shield also omits the registration/ownership/banking/shipping/
 * trade-reference packet, EXCEPT the Pharmacy Information section's four
 * fields, which the sheet keeps visible-but-optional (Not Required) rather
 * than omitting outright.
 */
const SHIELD = {
    ...Object.fromEntries(Object.keys(FULL_REGISTRATION).map((path) => [path, 'omit'])),
    'dateBusinessBegan': 'not-required',
    'pharmacyType': 'not-required',
    'dispensing.fillOutOfStateCustomers': 'not-required',
    'dispensing.outOfStateCustomersCount': 'not-required',
    'primary.psao': 'not-required',
};
/**
 * Multi-feature bundle rule from the sheet's legend: "apply the least
 * restrictive display + highest applicable requirement across the selected
 * features." A field is visible if ANY bundled column shows it, and required
 * if ANY bundled column that shows it also requires it.
 */
const combine = (cols) => {
    const fields = new Set();
    cols.forEach((c) => Object.keys(c).forEach((k) => fields.add(k)));
    const out = {};
    for (const field of fields) {
        const statuses = cols.map((c) => c[field] ?? 'omit');
        const visible = statuses.some((s) => s !== 'omit');
        out[field] = !visible ? 'omit' : statuses.some((s) => s === 'required') ? 'required' : 'not-required';
    }
    return out;
};
/** Legacy HubSpot option values -> current tier name. Mirrors the `ALIASES`
 * map in frontend/onboarding-v2/src/tiers.ts (PM-1917) — keep both in sync. */
const ALIASES = {
    'Basic Shopper': 'Explorer',
    'Premium Shopper': "Athena's Wisdom",
};
/** Resolution table keyed by the canonical HubSpot tier value (tiers.ts). */
const TABLES = {
    'Explorer': EXPLORER,
    "Athena's Wisdom": WISDOM,
    "Athena's Justice": JUSTICE,
    "Athena's Shield": SHIELD,
    "Athena's Arsenal: Wisdom + Justice": combine([WISDOM, JUSTICE]),
    "Athena's Arsenal: Wisdom + Shield": combine([WISDOM, SHIELD]),
    "Athena's Arsenal: Justice + Shield": combine([JUSTICE, SHIELD]),
    "Athena's Armor": combine([WISDOM, JUSTICE, SHIELD]),
};
/**
 * Resolve the field/document requirement map for a raw subscription-tier
 * value (as stored on the onboard record / HubSpot deal). An unrecognized,
 * blank, or absent tier resolves to an empty map — every field falls back to
 * its own default rule (the pre-PM-1833, full-registration behavior), so a
 * pharmacy onboarding without a resolved tier is never blocked or hidden.
 */
export const resolveOnboardingEntitlements = (rawTier) => {
    const trimmed = rawTier?.trim();
    if (!trimmed)
        return {};
    const name = ALIASES[trimmed] ?? trimmed;
    return TABLES[name] ?? {};
};
/**
 * Whether a tier's document packet should include wholesaler-owned
 * agreements (e.g. the Masters / Quest secondary-wholesaler applications) —
 * they pull prefill from ownership/banking/PIC/trade-reference fields, which
 * are omitted entirely for a reduced-registration tier. PM ('pm'-owned)
 * agreements (BAA, User Agreement) are unaffected — every tier signs those.
 */
export const tierNeedsWholesalerAgreements = (rawTier) => {
    const entitlements = resolveOnboardingEntitlements(rawTier);
    if (Object.keys(entitlements).length === 0)
        return true; // unresolved tier -> full packet
    return entitlements['owner.firstName'] !== 'omit';
};
